import { useState } from 'react';
import { Mail, TrendingUp, Users, Bell, Check, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Link } from 'react-router-dom';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const SERIF = "'Bodoni Moda', Georgia, serif";
const MONO  = "'IBM Plex Mono', monospace";

const TOPICS = [
  { key: 'weekly_updates',    label: 'Weekly talent market updates',        default: true  },
  { key: 'product_news',      label: 'Platform features & product news',    default: true  },
  { key: 'industry_insights', label: 'Fashion industry hiring insights',     default: true  },
  { key: 'launch_updates',    label: 'FashionSpyder launch & early access',  default: true  },
  { key: 'special_offers',    label: 'Founding member offers & promotions',  default: false },
];

const BENEFITS = [
  { icon: TrendingUp, title: 'Talent Market Pulse',  desc: 'Monthly data on which fashion roles are in demand, where, and at what seniority.' },
  { icon: Users,      title: 'Hiring Intelligence',  desc: 'Expert editorial on building fashion teams: what works, what doesn\'t, and what\'s changing.' },
  { icon: Bell,       title: 'Platform Insider',     desc: 'First to know about new FashionSpyder features, candidate additions, and early-access openings.' },
];

const Newsletter = () => {
  const [email, setEmail]           = useState('');
  const [name, setName]             = useState('');
  const [prefs, setPrefs]           = useState(
    Object.fromEntries(TOPICS.map(t => [t.key, t.default]))
  );
  const [loading, setLoading]       = useState(false);
  const [result, setResult]         = useState(null);
  const [error, setError]           = useState('');

  const togglePref = (key) => setPrefs(p => ({ ...p, [key]: !p[key] }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) { setError('Please enter your email.'); return; }
    setError(''); setLoading(true);
    try {
      const res  = await fetch(`${BACKEND}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, name, preferences: prefs }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Something went wrong.');
      setResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">

      {/* Hero */}
      <section className="py-20 lg:py-28 px-6 lg:px-20 border-b border-border">
        <div className="max-w-[1200px] mx-auto">
          <span className="text-xs uppercase tracking-widest text-muted-foreground block mb-5" style={{ fontFamily: MONO }}>
            Stay Informed
          </span>
          <h1 className="text-5xl lg:text-6xl font-semibold tracking-[-0.03em] leading-[1.05] mb-5 max-w-3xl" style={{ fontFamily: SERIF }}>
            Fashion hiring intelligence, straight to your inbox.
          </h1>
          <p className="text-lg text-muted-foreground max-w-xl leading-relaxed">
            No noise, no spam — only curated insights for HR professionals who take fashion hiring seriously.
          </p>
        </div>
      </section>

      <section className="py-16 px-6 lg:px-20">
        <div className="max-w-[1200px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">

            {/* Left: benefits */}
            <div className="lg:col-span-5 space-y-8">
              <h2 className="text-xl font-semibold" style={{ fontFamily: SERIF }}>
                What you'll receive
              </h2>
              {BENEFITS.map(({ icon: Icon, title, desc }) => (
                <div key={title} className="flex gap-4">
                  <div className="w-9 h-9 border border-border flex items-center justify-center flex-shrink-0">
                    <Icon className="w-4 h-4 text-[hsl(var(--primary))]" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm mb-1">{title}</p>
                    <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
                  </div>
                </div>
              ))}

              <div className="border border-border p-5 bg-[hsl(var(--secondary))]">
                <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3" style={{ fontFamily: MONO }}>
                  SendPulse integration coming soon
                </p>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Your subscription is stored in our database and will be automatically
                  migrated to SendPulse — Flence's mailing platform — when the integration
                  is ready. You won't need to re-subscribe.
                </p>
              </div>
            </div>

            {/* Right: form */}
            <div className="lg:col-span-7">
              {!result ? (
                <div className="border border-border p-8">
                  <h2 className="text-xl font-semibold mb-6" style={{ fontFamily: SERIF }}>
                    Subscribe
                  </h2>

                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div>
                      <label className="block text-xs uppercase tracking-wide text-muted-foreground mb-1.5" style={{ fontFamily: MONO }}>
                        Name (optional)
                      </label>
                      <Input
                        value={name}
                        onChange={e => setName(e.target.value)}
                        placeholder="Sofia Romano"
                        className="rounded-none h-10"
                      />
                    </div>

                    <div>
                      <label className="block text-xs uppercase tracking-wide text-muted-foreground mb-1.5" style={{ fontFamily: MONO }}>
                        Email Address <span className="text-[hsl(var(--primary))]">*</span>
                      </label>
                      <Input
                        type="email"
                        required
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        placeholder="sofia@maisonexample.com"
                        className="rounded-none h-10"
                        data-testid="newsletter-email-input"
                      />
                    </div>

                    <div>
                      <label className="block text-xs uppercase tracking-wide text-muted-foreground mb-3" style={{ fontFamily: MONO }}>
                        Topics
                      </label>
                      <div className="space-y-2.5">
                        {TOPICS.map(topic => (
                          <label
                            key={topic.key}
                            className="flex items-center gap-3 cursor-pointer group"
                          >
                            <Checkbox
                              checked={prefs[topic.key]}
                              onCheckedChange={() => togglePref(topic.key)}
                              className="rounded-none"
                            />
                            <span className="text-sm text-foreground group-hover:text-foreground/80 transition-colors">
                              {topic.label}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {error && (
                      <div className="border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                        {error}
                      </div>
                    )}

                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-foreground hover:bg-foreground/90 text-background h-11 gap-2 text-sm"
                      data-testid="newsletter-submit-btn"
                    >
                      <Mail className="w-4 h-4" />
                      {loading ? 'Subscribing…' : 'Subscribe'}
                    </Button>

                    <p className="text-xs text-muted-foreground" style={{ fontFamily: MONO }}>
                      No spam. Unsubscribe at any time. See our{' '}
                      <Link to="/privacy" className="underline underline-offset-2 hover:text-foreground">Privacy Policy</Link>.
                    </p>
                  </form>
                </div>
              ) : (
                /* Success */
                <div className="border border-border p-8">
                  <div className="w-11 h-11 border border-green-200 bg-green-50 flex items-center justify-center mb-6">
                    <Check className="w-5 h-5 text-green-600" />
                  </div>
                  <h2 className="text-2xl font-semibold mb-3" style={{ fontFamily: SERIF }}>
                    {result.already_subscribed ? 'Preferences updated.' : 'You\'re subscribed.'}
                  </h2>
                  <p className="text-muted-foreground leading-relaxed mb-8">
                    {result.message}
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <Button
                      variant="outline"
                      onClick={() => setResult(null)}
                      className="border-border hover:border-foreground gap-2"
                    >
                      Update preferences
                    </Button>
                    <Link to="/register">
                      <Button className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary)/0.88)] text-white border-0 gap-2">
                        Get Started <ArrowRight className="w-4 h-4" />
                      </Button>
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Newsletter;
